import { anmeldekopf, basisUrl, rufe } from './client';

/**
 * Die Nachweise für die Betriebsprüfung (Kap. 21.3 und 21.6).
 *
 * Alle drei Berichte lesen `protokoll.aenderung` — die Ablage, die das System
 * ohnehin führt. Es gibt keinen zweiten Bestand, der nur für die Prüfung
 * geführt würde; er wäre genau der, dem niemand glauben muss.
 */

export type OffenerBeleg = {
  readonly id: string;
  readonly rechnungsnummer: string | null;
  readonly belegart: string;
  readonly belegdatum: string;
  readonly kundenname: string;
};

export type Monatsstand = {
  readonly monat: string;
  readonly fristBis: string;
  readonly belege: readonly OffenerBeleg[];
  /** Negativ heißt: Die Frist ist verstrichen. */
  readonly tageVerbleibend: number;
};

export type Feldaenderung = {
  readonly feld: string;
  readonly vorher: string | null;
  readonly nachher: string | null;
};

export type Konfigurationsaenderung = {
  readonly id: string;
  readonly zeitpunkt: string;
  readonly benutzer: string | null;
  readonly gegenstand: string;
  readonly tabelle: string;
  readonly datensatz: string;
  readonly operation: 'INSERT' | 'UPDATE' | 'DELETE';
  readonly ausloeser: string | null;
  readonly vorgang: string | null;
  readonly felder: readonly Feldaenderung[];
};

export type Kontrollnachweis = {
  readonly kennung: string;
  readonly bezeichnung: string;
  readonly art: 'freigabe' | 'vieraugen' | 'plausibilitaet' | 'vollstaendigkeit' | 'abstimmung';
  readonly beschreibung: string;
  readonly wirkung: string;
  readonly anforderungen: readonly string[];
  readonly aktiv: boolean;
  readonly stillgelegtAm: string | null;
  readonly stillgelegtGrund: string | null;
  readonly nachweisTabelle: string;
  readonly nachweisSpalte: string | null;
  readonly ausfuehrungen: number;
  readonly imZeitraum: boolean;
  readonly zuletzt: string | null;
};

export type Rechtezeile = {
  readonly benutzer: string;
  readonly anmeldename: string;
  readonly name: string;
  readonly objekt: string;
  readonly aktion: string;
  readonly herkunft: string;
  readonly quelle: string;
  readonly von: string;
  readonly bis: string | null;
};

function spanne(von?: string, bis?: string): string {
  const teile = new URLSearchParams();
  if (von !== undefined && von !== '') teile.set('von', von);
  if (bis !== undefined && bis !== '') teile.set('bis', bis);
  const frage = teile.toString();
  return frage === '' ? '' : `?${frage}`;
}

/** Nicht festgeschriebene Belege je Belegmonat (BP-11, BP-12). */
export async function festschreibung(): Promise<{ readonly staende: readonly Monatsstand[] }> {
  return rufe('/betriebspruefung/festschreibung');
}

/** Die Änderungshistorie der steuerrelevanten Konfiguration (BP-40). */
export async function konfiguration(
  von?: string,
  bis?: string,
): Promise<{
  readonly von: string;
  readonly bis: string;
  readonly protokollBeginn: string | null;
  readonly gegenstaende: Readonly<Record<string, string>>;
  readonly aenderungen: readonly Konfigurationsaenderung[];
}> {
  return rufe(`/betriebspruefung/konfiguration${spanne(von, bis)}`);
}

/** Das interne Kontrollsystem mit dem Nachweis der Ausführung (BP-42). */
export async function kontrollen(
  von?: string,
  bis?: string,
): Promise<{
  readonly von: string;
  readonly bis: string;
  readonly kontrollen: readonly Kontrollnachweis[];
}> {
  return rufe(`/betriebspruefung/kontrollen${spanne(von, bis)}`);
}

/** Wer in einem Zeitraum welche Rechte hatte (BP-43). */
export async function rechte(
  von?: string,
  bis?: string,
  objekte?: readonly string[],
): Promise<{
  readonly von: string;
  readonly bis: string;
  readonly protokollBeginn: string | null;
  readonly zeilen: readonly Rechtezeile[];
}> {
  const teile = new URLSearchParams();
  if (von !== undefined && von !== '') teile.set('von', von);
  if (bis !== undefined && bis !== '') teile.set('bis', bis);
  if (objekte !== undefined && objekte.length > 0) teile.set('objekte', objekte.join(','));
  const frage = teile.toString();
  return rufe(`/betriebspruefung/rechte${frage === '' ? '' : `?${frage}`}`);
}

/**
 * ============================================================================
 * DER MITTELBARE ZUGRIFF (BP-03, Z2)
 * ============================================================================
 *
 * „Frei filterbar nach Zeitraum, Belegart, Steuersatz, Kunde und Konto, mit
 * Summenbildung und Drill-down bis zum Einzelbeleg."
 */
export type Z2Summe = {
  readonly belegart: string;
  readonly steuerschluessel: string | null;
  readonly steuerprozent: string | null;
  readonly konto: string | null;
  readonly belege: number;
  readonly netto: string;
  readonly steuer: string;
  readonly brutto: string;
};

export type Z2Beleg = {
  readonly id: string;
  readonly rechnungsnummer: string | null;
  readonly belegart: string;
  readonly belegdatum: string;
  readonly kunde: string | null;
  readonly kundenname: string;
  readonly netto: string;
  readonly steuer: string;
  readonly brutto: string;
};

export type Z2Merkmale = {
  readonly belegarten: readonly string[];
  readonly steuerschluessel: readonly { readonly schluessel: string;
    readonly bezeichnung: string }[];
  readonly konten: readonly string[];
};

export async function z2Merkmale(): Promise<Z2Merkmale> {
  return rufe('/betriebspruefung/auswertung/merkmale');
}

/**
 * Summen und Einzelbelege in EINER Antwort.
 *
 * Nicht zwei Aufrufe: Der Drill-down muss sich zur Summe darüber addieren,
 * und das hält nur, wenn beide aus demselben Augenblick stammen.
 */
export async function z2Auswertung(frage: {
  readonly von: string;
  readonly bis: string;
  readonly belegart?: string;
  readonly steuerschluessel?: string;
  readonly kunde?: string;
  readonly konto?: string;
}): Promise<{
  readonly summen: readonly Z2Summe[];
  readonly belege: readonly Z2Beleg[];
  readonly ohneKonto: number;
  readonly grenze: number;
}> {
  const teile = new URLSearchParams({ von: frage.von, bis: frage.bis });
  for (const feld of ['belegart', 'steuerschluessel', 'kunde', 'konto'] as const) {
    const wert = frage[feld];
    if (wert !== undefined && wert !== '') teile.set(feld, wert);
  }
  return rufe(`/betriebspruefung/auswertung?${teile.toString()}`);
}

/**
 * ============================================================================
 * DIE DATENTRAEGERUEBERLASSUNG (BP-04 bis BP-08)
 * ============================================================================
 */
export type Exportdatei = {
  readonly name: string;
  readonly zeilen: number;
  readonly pruefsumme: string;
  readonly groesseBytes: number;
  readonly ablageschluessel: string;
};

export type Exportvermerk = {
  readonly id: string;
  readonly von: string;
  readonly bis: string;
  readonly mandant: string;
  readonly bereiche: readonly string[];
  /** LEG-07: auf welche Belegart begrenzt. null heisst alle. */
  readonly belegart: string | null;
  readonly empfaenger: string | null;
  readonly istProbe: boolean;
  readonly angelegtAm: string;
  readonly angelegtVonName: string | null;
  readonly dateien: readonly Exportdatei[];
  readonly zeilenGesamt: string;
  readonly bemerkung: string | null;
};

export async function datentraegerBereiche(): Promise<{
  readonly bereiche: readonly { readonly kennung: string; readonly name: string;
    readonly beschreibung: string }[];
}> {
  return rufe('/betriebspruefung/datentraeger/bereiche');
}

export async function datentraeger(): Promise<{
  readonly vermerke: readonly Exportvermerk[];
}> {
  return rufe('/betriebspruefung/datentraeger');
}

/**
 * Einen Datenträger erzeugen (BP-04, BP-07, BP-08).
 *
 * `istProbe` macht daraus die Prüfsimulation — dann geht sie an niemanden,
 * und ein Empfänger wäre eine Übergabe, die nie stattfand.
 */
export async function datentraegerErzeugen(angabe: {
  readonly von: string;
  readonly bis: string;
  readonly bereiche: readonly string[];
  /** LEG-07: weggelassen heisst alle Belegarten — der Regelfall. */
  readonly belegart?: string;
  readonly empfaenger?: string;
  readonly istProbe: boolean;
}): Promise<Exportvermerk> {
  return rufe('/betriebspruefung/datentraeger', { verfahren: 'POST', rumpf: angabe });
}

/**
 * Eine Datei des Datenträgers holen.
 *
 * NICHT über `rufe` und nicht als einfacher Link: Die Antwort ist kein JSON,
 * sondern die Datei selbst — und der Weg verlangt den Anmeldeschlüssel, den
 * ein `<a href>` nicht mitschickt.
 */
export async function datentraegerDatei(id: string, name: string): Promise<Blob> {
  const antwort = await fetch(
    `${basisUrl()}/betriebspruefung/datentraeger/${encodeURIComponent(id)}`
      + `/dateien/${encodeURIComponent(name)}`,
    { headers: anmeldekopf() },
  );
  if (!antwort.ok) {
    let rumpf: unknown;
    try {
      rumpf = await antwort.json();
    } catch {
      rumpf = {
        kennung: 'unbekannt',
        meldung: { wasGeschah: 'Die Datei konnte nicht geholt werden.' },
      };
    }
    throw Object.assign(new Error('Datentraegerdatei fehlgeschlagen'), {
      status: antwort.status,
      ...(rumpf as object),
    });
  }
  return antwort.blob();
}
